/**
 * @module pages/PaymentPage
 * @description MOCKED Neon hand-off (brief §5: donor pays on Neon's secure
 * hosted payment, then returns to AFJ).
 *
 * // TEMPORARY: demo stand-in for the Neon CRM API v2 Orders + NeonPay step.
 *   What caused this: no Neon credentials in the demo (Neon API 401, loop 16),
 *   and a demo must never take a real payment.
 *   Why we chose it: a clearly labeled interstitial shows WHERE Neon takes over
 *   and lets the room test both outcomes the brief requires (success, failure),
 *   without imitating Neon's own pages or collecting any card data.
 *   Side-effects: none; no network calls, no card fields, nothing stored.
 */
import { useEffect, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { navigate } from "../app/router";
import { Icon } from "../components/Icons";
import type { CartLine, GiftReceipt } from "../domain/types";
import { formatUsd, plural } from "../lib/format";
import "./Checkout.css";

/** @returns an obviously-fake reference, e.g. "DEMO-7K3Q9". */
function demoReference(): string {
    return `DEMO-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
}

/** The simulated hand-off screen. */
export function PaymentPage(): JSX.Element {
    const { lines, totalUsd, donor, causeById, setReceipt, dispatchCart, setPaymentFailed } = useGiving();
    const [busy, setBusy] = useState<boolean>(false);

    // Nothing to pay for (e.g. a reload after success): back to browsing.
    // `busy` guards the success path, which clears the cart on its way out.
    useEffect(() => {
        if (lines.length === 0 && !busy) navigate("browse");
    }, [lines.length, busy]);

    /** Simulate Neon's result, then route like the real return URL would. */
    const simulate = (outcome: "success" | "failure"): void => {
        setBusy(true);
        window.setTimeout(() => {
            if (outcome === "failure") {
                setPaymentFailed(true);
                navigate("checkout");
                return;
            }
            const receipt: GiftReceipt = { reference: demoReference(), lines: [...lines], totalUsd, donor, completedAt: new Date().toISOString() };
            setPaymentFailed(false);
            setReceipt(receipt);
            dispatchCart({ type: "clear" });
            navigate("thank-you");
        }, 1200);
    };

    return (
        <main className="container payment">
            <div className="payment__card">
                <p className="badge badge--sample">Demo step · simulated</p>
                <span className="payment__lock"><Icon name="lock" size={30} /></span>
                <h1 className="payment__title">Handing you to Neon's secure payment</h1>
                <p className="payment__lead">
                    In the live site, you would now pay on Neon's secure payment page: one payment of <strong>{formatUsd(totalUsd)}</strong> for {plural(lines.length, "cause")}, recorded in AFJ's Neon CRM against each cause. Then you return here for your thank-you page.
                </p>

                <div className="payment__passes">
                    <p className="payment__passes-title">What AFJ's site passes to Neon</p>
                    <ul>
                        <li><span>Donor</span><span>{donor.firstName} {donor.lastName} · {donor.email}</span></li>
                        {lines.map((l: CartLine) => (
                            <li key={l.causeId}><span>{causeById(l.causeId)?.title ?? l.causeId}</span><span>{formatUsd(l.amountUsd)}</span></li>
                        ))}
                        <li className="payment__passes-total"><span>One payment</span><span>{formatUsd(totalUsd)}</span></li>
                    </ul>
                    <p className="payment__fine">Card details are entered only in Neon's secure fields. They never touch AFJ's site or ours.</p>
                </div>

                <p className="payment__try">For this demo, choose an outcome. No payment is taken.</p>
                <div className="payment__actions">
                    <button type="button" className="btn btn--primary btn--lg" disabled={busy} onClick={() => simulate("success")}>
                        {busy ? "Confirming…" : <>Simulate successful payment <Icon name="check" /></>}
                    </button>
                    <button type="button" className="btn btn--ghost btn--lg" disabled={busy} onClick={() => simulate("failure")}>Simulate a declined card</button>
                </div>
            </div>
        </main>
    );
}
